/* OHROSE — cart drawer focus + Escape, round 7 items 2 and 3.
 *
 * Shrine's <cart-drawer> binds its keyup Escape handler to the element itself
 * and only calls trapFocus from a `transitionend` listener registered with
 * {once:true}. When that transition does not fire — reduced motion, an
 * interrupted open, a restyled transition property — focus never enters the
 * drawer, so the Escape keyup fires on an element outside the drawer subtree
 * and never bubbles through the listener. One defect, two symptoms.
 *
 * This layer is additive: it does not replace open()/close(), it observes the
 * `active` class and supplies the missing behaviour. Shrine's own trapFocus
 * still runs when it can; doing it twice is harmless.
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]), textarea:not([disabled]), summary, iframe, object, embed,' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable]';

  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), visible);
  }

  function init(drawer) {
    // R9: the guard used to be drawer.dataset.ohrFocus. The Section Rendering
    // API hands back a BRAND NEW <cart-drawer> node on every cart mutation, so
    // the dataset was empty each time and init() re-ran, permanently stacking
    // another set of document listeners and another MutationObserver. Track the
    // element itself on window, and tear the previous binding down first.
    if (window.__ohrDrawerHost === drawer) return;
    if (window.__ohrDrawerTeardown) {
      try { window.__ohrDrawerTeardown(); } catch (e) {}
    }
    window.__ohrDrawerHost = drawer;
    drawer.dataset.ohrFocus = 'true';

    var lastTrigger = null;
    // The inert set has to survive a host swap, or a drawer that was open when
    // the section re-rendered leaves every <body> child inert and the page dead.
    var inerted = window.__ohrDrawerInert || (window.__ohrDrawerInert = []);
    var listeners = [];
    var observers = [];
    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      listeners.push([target, type, fn, opts]);
    }
    window.__ohrDrawerTeardown = function () {
      listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      listeners.length = 0;
      observers.forEach(function (o) { o.disconnect(); });
      observers.length = 0;
    };

    // Remember what opened the drawer, whichever path was used.
    on(
      document,
      'click',
      function (e) {
        var t = e.target.closest && e.target.closest(
          '#cart-icon-bubble, .header__icon--cart, a[href$="/cart"], [name="add"], .product-form__submit, .atc-button, .sticky-atc__add_button, .pu__add'
        );
        // closest() returns the NEAREST match, which for the cart icon is the
        // #cart-icon-bubble <span> — visible but not focusable, so focus() on it
        // is a silent no-op. Walk up to the control that can actually hold focus.
        if (t) lastTrigger = t.closest('a[href], button, [tabindex]:not([tabindex="-1"])') || t;
      },
      true
    );

    function isOpen() {
      return drawer.classList.contains('active');
    }

    function setInert(on) {
      if (on) {
        // R21: do NOT reset the list here. The Section Rendering API can hand
        // back a fresh <cart-drawer> while the drawer is open; init() then calls
        // this again, and clearing first — combined with the
        // `already has inert -> skip` guard below — wiped the record of what we
        // had disabled and re-added nothing. Close then un-inerted an empty list
        // and left the whole page inert with no way back but a reload.
        // Track additively instead: skip what we already own, and never touch
        // something made inert for someone else's reason.
        Array.prototype.forEach.call(document.body.children, function (el) {
          if (el === drawer || el.contains(drawer) || el.tagName === 'SCRIPT') return;
          if (inerted.indexOf(el) !== -1) return;
          if (el.hasAttribute('inert')) return;
          el.setAttribute('inert', '');
          el.setAttribute('aria-hidden', 'true');
          inerted.push(el);
        });
        var main = document.getElementById('MainContent');
        if (main && inerted.indexOf(main) === -1 && !main.hasAttribute('inert')) {
          main.setAttribute('inert', '');
          main.setAttribute('aria-hidden', 'true');
          inerted.push(main);
        }
      } else {
        inerted.forEach(function (el) {
          el.removeAttribute('inert');
          el.removeAttribute('aria-hidden');
        });
        inerted.length = 0;   // R21: NOT `inerted = []` — that rebinds the local
                              // and orphans window.__ohrDrawerInert, so the set
                              // never crosses a host swap and close un-inerts nothing.
      }
    }

    function focusInside() {
      var close = drawer.querySelector('.drawer__header .drawer__close') ||
                  drawer.querySelector('.drawer__close');
      var target = (close && visible(close) ? close : null) ||
                   focusables(drawer)[0] ||
                   drawer.querySelector('.drawer__inner');
      if (!target) return false;
      try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
      return drawer.contains(document.activeElement);
    }

    function onOpen() {
      setInert(true);
      // Move focus inside so Escape has somewhere to bubble from, and so a
      // screen reader lands in the dialog rather than behind it. The panel is
      // visibility:hidden until its transition starts, and .focus() is a no-op
      // on a hidden element — so retry across the open animation instead of
      // relying on a single frame or on a transitionend that may never fire.
      var tries = 0;
      (function attempt() {
        if (!isOpen()) return;
        if (drawer.contains(document.activeElement)) return;
        if (focusInside()) return;
        if (++tries > 40) return;
        setTimeout(attempt, 25);
      })();
      drawer.addEventListener('transitionend', function once() {
        drawer.removeEventListener('transitionend', once);
        if (isOpen() && !drawer.contains(document.activeElement)) focusInside();
      });
    }

    function onClose() {
      setInert(false);
      var t = lastTrigger;
      if (!t || !visible(t)) t = document.querySelector('a.header__icon--cart');
      if (!t || !visible(t)) {
        var bubble = document.querySelector('#cart-icon-bubble');
        t = bubble && (bubble.closest('a[href], button') || null);
      }
      if (!t) {
        if (drawer.contains(document.activeElement)) document.activeElement.blur();
        return;
      }
      // Shrine's own removeTrapFocus fires while the header is still inert, so
      // its restore lands on <body>; and the closing transition can blur again
      // afterwards. Re-assert across the close animation until it sticks.
      var tries = 0;
      (function restore() {
        if (isOpen()) return;
        if (document.activeElement === t) return;
        try { t.focus({ preventScroll: true }); } catch (err) { t.focus(); }
        if (document.activeElement === t) return;
        if (++tries > 20) return;
        setTimeout(restore, 40);
      })();
    }

    var wasOpen = isOpen();
    var mo = new MutationObserver(function () {
      var open = isOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      open ? onOpen() : onClose();
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    observers.push(mo);
    if (wasOpen) onOpen();

    // Escape from anywhere, and Tab kept inside the panel.
    on(document, 'keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        // guard: close() comes from the vendor class, not from this file
        if (typeof drawer.close !== 'function') return;
        e.preventDefault();
        drawer.close();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables(drawer);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (!drawer.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Belt and braces: keyup too, matching Shrine's own binding.
    on(document, 'keyup', function (e) {
      if (isOpen() && (e.key === 'Escape' || e.key === 'Esc') && typeof drawer.close === 'function') drawer.close();
    });
  }

  function boot() {
    var d = document.querySelector('cart-drawer');
    if (d) init(d);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // The drawer host is re-rendered by the Section Rendering API on cart updates.
  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('load', boot);
})();

/* R8 item 7 — cart drawer upsell rail.
 * Fetches Shopify's own product recommendations section and injects it inside
 * the drawer body, above the footer. No app, no third-party origin. Renders
 * nothing when the response has no cards, and re-fetches whenever the cart
 * changes so the rail never recommends something already in the bag. */
(function () {
  'use strict';
  if (window.customElements && !customElements.get('cart-upsell')) {
    customElements.define('cart-upsell', class extends HTMLElement {
      connectedCallback() { this.load(); }
      load() {
        var url = this.dataset.url;
        if (!url || this.dataset.loading === 'true') return;
        this.dataset.loading = 'true';
        fetch(url)
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            var box = document.createElement('div');
            box.innerHTML = html;
            var rail = box.querySelector('[data-cu-wrap]');
            var cards = rail ? rail.querySelectorAll('.ohr-cu__card').length : 0;
            if (!cards) { this.innerHTML = ''; this.hidden = true; return; }
            this.hidden = false;
            this.innerHTML =
              '<p class="ohr-cu__heading">' + (this.dataset.heading || '') + '</p>' + rail.outerHTML;
          }.bind(this))
          .catch(function () { this.innerHTML = ''; this.hidden = true; }.bind(this))
          .finally(function () { this.dataset.loading = 'false'; }.bind(this));
      }
    });
  }
  // the drawer body is re-rendered by the Section Rendering API on every cart
  // change, which re-runs connectedCallback on a fresh element — nothing to do.
})();
